import { ClientPaginatedTable } from '@avanta/shared/components/ClientPaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type TableFilterOption } from '@avanta/shared/types/table';
import { getCenterName, getStatusBadge } from '@avanta/shared/utils/appointments';
import { type ColumnDef } from '@tanstack/react-table';
import { Calendar } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SESSION_DISPLAY_FORMAT } from 'constants/date';
import { type Appointment } from 'types/appointments';
import { type Report } from 'types/files';
import { formatUnifiedForDisplay } from 'utils/date';
import { isCompletedAppointment } from '../../utils/appointments';
import { useFetchReportsHistory } from '../ReportsHistory/useFetchReportsHistory';
import { SessionTableActions } from './SessionTableActions';

const SESSION_FILTER = {
  COMPLETED: 'completed',
  UPCOMING: 'upcoming',
  CANCELED: 'canceled',
} as const;

type SessionFilterKey = (typeof SESSION_FILTER)[keyof typeof SESSION_FILTER];

interface AppointmentsHistoryProps {
  patientId: string;
  appointments: Appointment[];
  isLoading: boolean;
}

const getFilterOptions = (): TableFilterOption<SessionFilterKey>[] => [
  {
    key: SESSION_FILTER.COMPLETED,
    label: t('AppointmentsHistory.filters.completed', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: SESSION_FILTER.UPCOMING,
    label: t('AppointmentsHistory.filters.upcoming', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: SESSION_FILTER.CANCELED,
    label: t('AppointmentsHistory.filters.canceled', { ns: LOCIZE_NS.INSURANCE }),
  },
];

const matchesSessionFilter = (row: Appointment, selectedKey: SessionFilterKey): boolean => {
  if (selectedKey === SESSION_FILTER.CANCELED) return row.canceled;
  if (selectedKey === SESSION_FILTER.COMPLETED) return isCompletedAppointment(row);
  return !row.canceled && !isCompletedAppointment(row);
};

const getTableColumns = (reports: Report[]): ColumnDef<Appointment>[] => [
  {
    id: 'date',
    accessorKey: 'dateTime',
    enableSorting: true,
    header: t('AppointmentsTable.date.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row => formatUnifiedForDisplay(row.dateTime, SESSION_DISPLAY_FORMAT),
    cell: ({ row }) => formatUnifiedForDisplay(row.original.dateTime, SESSION_DISPLAY_FORMAT),
  },
  {
    id: 'psychologist',
    accessorKey: 'psychologist',
    header: t('AppointmentsTable.psychologist.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row =>
      `${row.psychologist?.psychologist?.name ?? ''} ${row.psychologist?.psychologist?.surname ?? ''}`.trim(),
    cell: ({ row }) =>
      `${row.original.psychologist?.psychologist?.name ?? ''} ${row.original.psychologist?.psychologist?.surname ?? ''}`.trim(),
  },
  {
    id: 'type',
    accessorKey: 'bookedContactMethod',
    header: t('AppointmentsTable.type.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row =>
      t(`AppointmentCard.types.${row.bookedContactMethod}`, { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }) =>
      t(`AppointmentCard.types.${row.original.bookedContactMethod}`, {
        ns: LOCIZE_NS.APPOINTMENTS,
      }),
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: t('AppointmentsTable.status.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }) => getStatusBadge(row.original),
  },
  {
    id: 'center',
    accessorKey: 'center',
    header: t('AppointmentsTable.center.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    accessorFn: row => getCenterName(row.bookedContactMethod, row.center),
    cell: ({ row }) => getCenterName(row.original.bookedContactMethod, row.original.center),
  },
  {
    id: 'actions',
    header: t('AppointmentsTable.actions.label', { ns: LOCIZE_NS.APPOINTMENTS }),
    cell: ({ row }) => <SessionTableActions appointment={row.original} reports={reports} />,
  },
];

export const AppointmentsHistory = ({
  patientId,
  appointments,
  isLoading,
}: AppointmentsHistoryProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { data: reports } = useFetchReportsHistory(patientId);
  const columns = useMemo(() => getTableColumns(reports ?? []), [reports]);

  return (
    <ClientPaginatedTable<Appointment, SessionFilterKey>
      data={appointments}
      columns={columns}
      isLoading={isLoading}
      emptyTitle={t('Common.noResultsFound', { ns: LOCIZE_NS.COMMON })}
      title={t('AppointmentsHistory.heading')}
      icon={Calendar}
      tableClassName="w-full"
      withSearch
      searchPlaceholder="AppointmentsHistory.search.placeholder"
      searchPlaceholderNs={LOCIZE_NS.INSURANCE}
      initialSorting={[{ id: 'date', desc: true }]}
      filter={{
        accessorKey: 'canceled',
        options: getFilterOptions(),
        filterFn: matchesSessionFilter,
      }}
    />
  );
};
